import React, { useEffect, useState } from "react";
import { PortalHeader, PortalBody, StatTile, EmptyState } from "@/components/PortalUI";
import api, { formatApiError } from "@/lib/api";
import { CreditCard, ExternalLink, Target, Code2, Copy, Check } from "lucide-react";

// ============ Billing ============
export function ClientBilling() {
  const [state, setState] = useState({ subscription: null, invoices: [] });
  const [portalBusy, setPortalBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = () => api.get("/billing/me").then((r) => setState(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openPortal = async () => {
    setPortalBusy(true); setErr("");
    try {
      const { data } = await api.post("/payments/portal", { return_url: window.location.origin + "/client/billing" });
      window.location.href = data.portal_url;
    } catch (e) { setErr(formatApiError(e)); }
    finally { setPortalBusy(false); }
  };

  const sub = state.subscription;
  return (
    <>
      <PortalHeader eyebrow="/ CLIENT" title="Billing" testId="client-billing-header">
        {sub && sub.stripe_customer_id && (
          <button className="dt6-btn" onClick={openPortal} disabled={portalBusy} data-testid="billing-portal-btn">
            <CreditCard size={14} /> {portalBusy ? "REDIRECTING..." : "Manage subscription"}
          </button>
        )}
      </PortalHeader>
      <PortalBody>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <StatTile label="Current tier" value={sub?.tier?.toUpperCase() || "—"} hint={sub?.status || "no subscription"} testId="billing-tier" />
          <StatTile label="Records used" value={(sub?.current_period_records_used || 0).toLocaleString()} hint={sub ? `of ${(sub.records_per_month || 0).toLocaleString()}` : "—"} testId="billing-records" />
          <StatTile label="Concurrent lanes" value={sub?.concurrent_lanes || "—"} testId="billing-lanes" />
        </div>

        {!sub || sub.status === "cancelled" ? (
          <div className="dt6-card p-8" data-testid="billing-no-sub">
            <div className="dt6-chip mb-3">/ NO SUBSCRIPTION</div>
            <h3 className="font-heading text-2xl font-bold">Pick a tier.</h3>
            <p className="text-[color:var(--dt6-muted)] mt-2">Monthly retainers with cycle-based licensing. Upgrade or downgrade any time via the Stripe customer portal.</p>
            <div className="mt-6">
              <a href="/pricing" className="dt6-btn dt6-btn-primary" data-testid="billing-goto-pricing">Go to pricing</a>
            </div>
          </div>
        ) : (
          <div className="dt6-card p-6 mb-6">
            <div className="dt6-chip dt6-chip-primary mb-3">/ ACTIVE</div>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-heading text-2xl font-bold">{sub.tier?.toUpperCase()}</div>
                <div className="text-sm text-[color:var(--dt6-muted)] mt-1">Started {sub.started_at ? new Date(sub.started_at).toLocaleDateString() : "—"}</div>
              </div>
              <div className="text-right">
                <div className="dt6-chip dt6-chip-success">{sub.status?.toUpperCase()}</div>
              </div>
            </div>
          </div>
        )}

        <h2 className="font-heading text-xl font-bold mb-3">Invoices</h2>
        {(!state.invoices || state.invoices.length === 0) ? (
          <EmptyState testId="billing-invoices-empty">NO INVOICES YET.</EmptyState>
        ) : (
          <div className="dt6-card overflow-hidden">
            <table className="dt6-table">
              <thead><tr><th>Number</th><th>Date</th><th>Status</th><th>Amount</th><th></th></tr></thead>
              <tbody>
                {state.invoices.map((i) => (
                  <tr key={i.id} data-testid={`invoice-row-${i.id}`}>
                    <td className="font-mono text-xs">{i.number || i.id.slice(0,10)}</td>
                    <td className="text-xs">{i.created ? new Date(i.created * 1000).toLocaleDateString() : "—"}</td>
                    <td><span className="dt6-chip">{i.status?.toUpperCase()}</span></td>
                    <td className="font-mono">${((i.amount_paid || 0) / 100).toFixed(2)}</td>
                    <td>
                      {i.hosted_invoice_url && (
                        <a className="dt6-link text-xs inline-flex items-center gap-1" href={i.hosted_invoice_url} target="_blank" rel="noreferrer" data-testid={`invoice-view-${i.id}`}>
                          <ExternalLink size={12} /> view
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {err && <div className="mt-4 text-sm text-[color:var(--dt6-danger)]" data-testid="billing-error">{err}</div>}
      </PortalBody>
    </>
  );
}

// ============ Universe Counts ============
export function ClientUniverseCounts() {
  const [form, setForm] = useState({ state: "", county: "", age_min: "", age_max: "", party_lean: "", turnout_score_min: "", include_cell: true, include_email: false });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [err, setErr] = useState("");

  const load = () => api.get("/client/universe/history").then((r) => setHistory(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setResult(null);
    try {
      const payload = { ...form };
      ["age_min","age_max","turnout_score_min"].forEach((k) => {
        if (payload[k] === "") delete payload[k]; else payload[k] = parseInt(payload[k], 10);
      });
      if (!payload.party_lean) delete payload.party_lean;
      if (!payload.county) delete payload.county;
      const { data } = await api.post("/client/universe/count", payload);
      setResult(data);
      await load();
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <PortalHeader eyebrow="/ CLIENT" title="Universe counts" testId="universe-header" />
      <PortalBody>
        <div className="dt6-card p-6 mb-6">
          <div className="dt6-chip mb-4">/ RUN A COUNT</div>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="universe-form">
            <div><label className="dt6-label">State</label><input className="dt6-input" required value={form.state} onChange={(e)=>setForm({...form,state:e.target.value.toUpperCase().slice(0,2)})} data-testid="univ-state" /></div>
            <div><label className="dt6-label">County (optional)</label><input className="dt6-input" value={form.county} onChange={(e)=>setForm({...form,county:e.target.value})} data-testid="univ-county" /></div>
            <div><label className="dt6-label">Party lean (R/D/U)</label><input className="dt6-input" value={form.party_lean} onChange={(e)=>setForm({...form,party_lean:e.target.value.toUpperCase().slice(0,1)})} data-testid="univ-party" /></div>
            <div><label className="dt6-label">Age min</label><input className="dt6-input" type="number" value={form.age_min} onChange={(e)=>setForm({...form,age_min:e.target.value})} data-testid="univ-age-min" /></div>
            <div><label className="dt6-label">Age max</label><input className="dt6-input" type="number" value={form.age_max} onChange={(e)=>setForm({...form,age_max:e.target.value})} data-testid="univ-age-max" /></div>
            <div><label className="dt6-label">Turnout score min</label><input className="dt6-input" type="number" value={form.turnout_score_min} onChange={(e)=>setForm({...form,turnout_score_min:e.target.value})} data-testid="univ-turnout" /></div>
            <div className="md:col-span-3 flex items-center gap-6 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" checked={form.include_cell} onChange={(e)=>setForm({...form,include_cell:e.target.checked})} data-testid="univ-cell" /> Include cell phones</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={form.include_email} onChange={(e)=>setForm({...form,include_email:e.target.checked})} data-testid="univ-email" /> Include email</label>
            </div>
            {err && <div className="md:col-span-3 text-sm text-[color:var(--dt6-danger)]">{err}</div>}
            <div className="md:col-span-3 flex justify-between items-center">
              <div className="text-xs font-mono tracking-[0.14em] uppercase text-[color:var(--dt6-muted)]">
                / D1 (VOTER FILE) — ADAPTER STATE: NOT_CONNECTED → CONCIERGE QUEUE
              </div>
              <button type="submit" className="dt6-btn dt6-btn-primary" disabled={busy || !form.state} data-testid="univ-submit">
                <Target size={14} /> {busy ? "COUNTING..." : "Run count"}
              </button>
            </div>
          </form>
          {result && (
            <div className="mt-6 dt6-card p-4" style={{ background: "var(--dt6-bg)" }} data-testid="universe-result">
              <div className="font-mono text-[0.7rem] tracking-[0.14em] uppercase text-[color:var(--dt6-muted)]">/ RESULT · {result.id.slice(0,8).toUpperCase()}</div>
              <div className="mt-2 flex items-center gap-3">
                <span className={`dt6-chip ${result.result?.mode === 'SANDBOX' ? 'dt6-chip-amber' : 'dt6-chip'}`}>{result.result?.status || "QUEUED"}</span>
                <span className="text-sm text-[color:var(--dt6-muted)]">{result.result?.note}</span>
              </div>
            </div>
          )}
        </div>

        <h2 className="font-heading text-xl font-bold mb-3">Recent counts</h2>
        {history.length === 0 ? (
          <EmptyState testId="universe-history-empty">NO COUNTS YET.</EmptyState>
        ) : (
          <div className="dt6-card overflow-hidden">
            <table className="dt6-table">
              <thead><tr><th>When</th><th>State</th><th>Criteria</th><th>Status</th></tr></thead>
              <tbody>{history.map((h) => (
                <tr key={h.id} data-testid={`univ-history-${h.id}`}>
                  <td className="font-mono text-xs">{new Date(h.created_at).toLocaleString()}</td>
                  <td className="font-mono">{h.parameters?.state}</td>
                  <td className="text-xs text-[color:var(--dt6-muted)]">{JSON.stringify(h.parameters).slice(0, 80)}...</td>
                  <td><span className="dt6-chip">{h.result?.status}</span></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </PortalBody>
    </>
  );
}

// ============ Pixel Manager ============
export function ClientPixel() {
  const [sites, setSites] = useState([]);
  const [selected, setSelected] = useState(null);
  const [snippet, setSnippet] = useState("");
  const [events, setEvents] = useState([]);
  const [visitors, setVisitors] = useState({ resolved: [], identity_adapter_state: "NOT_CONNECTED" });
  const [form, setForm] = useState({ name: "", domain: "" });
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");

  const load = () => api.get("/pixel/sites").then((r) => setSites(r.data));
  useEffect(() => { load(); }, []);

  const select = async (s) => {
    setSelected(s);
    setSnippet("");
    setEvents([]);
    setVisitors({ resolved: [] });
    try {
      const snip = await api.get(`/pixel/sites/${s.id}/snippet`);
      setSnippet(snip.data);
      const evts = await api.get(`/pixel/sites/${s.id}/events`);
      setEvents(evts.data);
      const rv = await api.get(`/pixel/sites/${s.id}/resolved-visitors`);
      setVisitors(rv.data);
    } catch (e) { setErr(formatApiError(e)); }
  };

  const create = async (e) => {
    e.preventDefault(); setErr("");
    try {
      const { data } = await api.post("/pixel/sites", form);
      setForm({ name: "", domain: "" });
      await load();
      select(data);
    } catch (e2) { setErr(formatApiError(e2)); }
  };

  const copySnippet = () => {
    navigator.clipboard.writeText(snippet).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  };

  return (
    <>
      <PortalHeader eyebrow="/ CLIENT" title="Pixel" testId="pixel-header" />
      <PortalBody>
        <div className="mb-6 text-xs font-mono tracking-[0.14em] uppercase text-[color:var(--dt6-muted)]">
          / IDENTITY RESOLUTION (D3) STATE: <span className="text-[color:var(--dt6-secondary)]">{visitors.identity_adapter_state || "NOT_CONNECTED"}</span> · UNTIL LIVE, RESOLVED VISITORS = MATCHED FORM SUBMITS ONLY
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="dt6-card p-4 mb-4">
              <div className="dt6-chip mb-2">+ ADD SITE</div>
              <form onSubmit={create} className="space-y-3" data-testid="pixel-add-form">
                <div><label className="dt6-label">Name</label><input className="dt6-input" required value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="pixel-name" /></div>
                <div><label className="dt6-label">Domain</label><input className="dt6-input" required value={form.domain} onChange={(e)=>setForm({...form,domain:e.target.value})} placeholder="mycampaign.com" data-testid="pixel-domain" /></div>
                <button className="dt6-btn dt6-btn-primary w-full justify-center text-xs" data-testid="pixel-create-site">Create site key</button>
              </form>
            </div>
            {sites.length === 0 ? (
              <EmptyState testId="pixel-sites-empty">NO SITES YET.</EmptyState>
            ) : (
              <div className="space-y-2">
                {sites.map((s) => (
                  <button key={s.id} onClick={() => select(s)} className={`dt6-card w-full text-left p-3 ${selected?.id === s.id ? "border-[color:var(--dt6-primary)]" : ""}`} data-testid={`pixel-site-${s.id}`}>
                    <div className="font-heading font-bold text-sm">{s.name}</div>
                    <div className="text-xs text-[color:var(--dt6-muted)]">{s.domain}</div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className={`dt6-chip text-[0.6rem] ${s.verified ? "dt6-chip-success" : ""}`}>{s.verified ? "VERIFIED" : "PENDING"}</span>
                      <span className="font-mono text-[0.65rem] text-[color:var(--dt6-muted)]">{s.event_count} events</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="md:col-span-2">
            {!selected ? (
              <EmptyState testId="pixel-select-prompt">SELECT A SITE TO VIEW SNIPPET AND EVENTS.</EmptyState>
            ) : (
              <>
                <div className="dt6-card p-4 mb-4" data-testid="pixel-snippet">
                  <div className="flex items-center justify-between mb-2">
                    <div className="dt6-chip">/ SNIPPET</div>
                    <button className="dt6-btn text-xs" onClick={copySnippet} data-testid="pixel-copy">
                      {copied ? <><Check size={12} /> COPIED</> : <><Copy size={12} /> Copy</>}
                    </button>
                  </div>
                  <pre className="text-[0.72rem] leading-relaxed p-3 rounded font-mono overflow-x-auto whitespace-pre-wrap break-all" style={{ background: "var(--dt6-bg)", border: "1px solid var(--dt6-border)" }}>
{snippet}
                  </pre>
                </div>
                <div className="dt6-card p-4 mb-4" data-testid="pixel-events-panel">
                  <div className="dt6-chip mb-2">/ LIVE EVENTS ({events.length})</div>
                  {events.length === 0 ? (
                    <EmptyState testId="pixel-events-empty">NO EVENTS RECEIVED. PASTE THE SNIPPET INTO YOUR SITE AND LOAD A PAGE.</EmptyState>
                  ) : (
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {events.map((e) => (
                        <div key={e.id} className="text-xs border-l-2 pl-3 py-1" style={{ borderColor: "var(--dt6-primary)" }}>
                          <span className="font-mono uppercase mr-2">{e.event_type}</span>
                          <span className="text-[color:var(--dt6-muted)]">{e.url || ""}</span>
                          <div className="text-[0.65rem] font-mono text-[color:var(--dt6-muted)]">{new Date(e.created_at).toLocaleString()}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="dt6-card p-4">
                  <div className="dt6-chip mb-2">/ RESOLVED VISITORS ({visitors.resolved?.length || 0})</div>
                  {(!visitors.resolved || visitors.resolved.length === 0) ? (
                    <EmptyState testId="pixel-visitors-empty">NO RESOLVED CONTACTS YET.</EmptyState>
                  ) : (
                    <table className="dt6-table">
                      <thead><tr><th>When</th><th>Email</th><th>UTM</th></tr></thead>
                      <tbody>
                        {visitors.resolved.map((v) => (
                          <tr key={v.id} data-testid={`pixel-visitor-${v.id}`}>
                            <td className="font-mono text-xs">{new Date(v.created_at).toLocaleString()}</td>
                            <td className="text-xs">{v.identity?.email || "—"}</td>
                            <td className="font-mono text-xs text-[color:var(--dt6-muted)]">{JSON.stringify(v.utm || {})}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        {err && <div className="mt-4 text-sm text-[color:var(--dt6-danger)]">{err}</div>}
      </PortalBody>
    </>
  );
}
